import { Translations } from '../types'

// English — the alternate language. Typed as Translations, so the compiler
// rejects this file if any key from the canonical Norwegian dictionary is
// missing or misspelled here.
export const en: Translations = {
  'nav.home': 'Home',
  'nav.about': 'About',
  'nav.toggle': 'Toggle navigation',
  'nav.beamDesigner.locked': 'Sign in to access Beam Designer',
  'nav.signInPrompt': 'Please sign in to access this feature.',

  'app.wood.beamLabel': 'Wood Beam Designer',
  'app.concrete.beamLabel': 'Concrete Beam Designer',
  'app.steel.beamLabel': 'Steel Beam Designer',

  'lang.label': 'Language',
  'lang.norwegian': 'Norwegian',
  'lang.english': 'English',

  // ===== Beam designer — workspace chrome =====
  'beam.tab.geometry': 'Geometry',
  'beam.tab.materials': 'Materials',
  'beam.tab.loads': 'Loads',
  'beam.tab.results': 'Analysis',
  'beam.tab.response': 'Results',
  'beam.tab.report': 'Report',
  'beam.results.deflectionNoCreepNoteTimber': 'Deflections on this tab are instantaneous elastic deflections (w_inst) without creep. The final deflection w_fin = w_inst + k_def·w_qp per EN 1995-1-1 §2.2.3 (k_def from the service class, w_qp from the quasi-permanent combination) is shown on the Results tab.',
  'beam.results.deflectionNoCreepNoteConcrete': 'Deflections on this tab are elastic for the uncracked section with E_cm, without creep or cracking. The instantaneous deflection w_inst (cracked, ζ method) and the final deflection w_fin with creep (E_c,eff = E_cm/(1+φ)) per EN 1992-1-1 §7.4.3 are shown on the Results tab.',
  'beam.tab.ariaLabel': '{title} tab',

  'beam.header.geometry': 'Beam Geometry',
  'beam.header.materials': 'Material Properties',
  'beam.header.loads': 'Load Application',
  'beam.header.response': 'Response & Utilization',
  'beam.header.report': 'Design Report',
  'beam.header.results': 'Analysis Results',

  'beam.timberType.label': 'Timber Type:',
  'beam.timberType.selection': 'Timber type selection',
  'beam.timberType.solid': 'Solid Wood',
  'beam.timberType.glulam': 'Glulam',

  'beam.status.loading': 'Loading...',
  'beam.status.analysisRunning': 'Analysis Running',
  'beam.status.analyzingModel': 'Analyzing beam model...',

  // ===== Beam designer — geometry tab =====
  'beam.geometry.dismissNotice': 'Dismiss notice',
  'beam.geometry.beamSpans': 'Beam Spans ({count})',
  'beam.geometry.addSpan': 'Add Span',
  'beam.geometry.currentSpans': 'Current Spans',
  'beam.geometry.spanNumber': 'Span {n}',
  'beam.geometry.spanMeta': 'Node {left} → Node {right} | E: {e} GPa | A: {a} cm² | I: {i} cm⁴',
  'beam.geometry.loadsApplied': '{count} load(s) applied',
  'beam.geometry.removeSpan': 'Remove span',
  'beam.geometry.supportConditions': 'Support Conditions',
  'beam.geometry.node': 'Node {id}:',
  'beam.geometry.bearingLength': 'Bearing length (mm)',
  'beam.geometry.bearingLengthHint': 'Contact length at the support — enables the timber compression-perpendicular check (EC5 §6.1.5)',
  'beam.support.fixed': 'Fixed',
  'beam.support.pinned': 'Pinned',
  'beam.support.roller': 'Roller',
  'beam.support.free': 'Free',
  'beam.geometry.crossSection': 'Cross Section',
  'beam.geometry.loadingCatalog': 'Loading catalog from server...',
  'beam.geometry.catalogError': '{error}. Using fallback presets.',
  'beam.geometry.selectPresetPlaceholder': 'Select standard section …',
  'beam.geometry.custom': 'Custom',
  'beam.geometry.customSectionHint': 'Custom section mode: Enter dimensions manually. Area and inertia will be calculated for rectangular sections.',
  'beam.geometry.width': 'Width (mm):',
  'beam.geometry.height': 'Height (mm):',
  'beam.geometry.webThickness': 'Web Thickness (mm):',
  'beam.geometry.flangeThickness': 'Flange Thickness (mm):',
  'beam.geometry.optionalIH': '(optional, for I/H beams)',
  'beam.geometry.egExample6': 'e.g. 6.0',
  'beam.geometry.egExample10': 'e.g. 10.0',
  'beam.geometry.webFlangeHint': 'Web and flange thickness are used for section classification (EN 1993-1-1) and local buckling checks.',
  'beam.geometry.calculatedProps': 'Calculated properties (rectangular):',
  'beam.geometry.areaValue': 'Area: {cm2} cm² ({mm2} mm²)',
  'beam.geometry.inertiaValue': 'Inertia: {cm4} cm⁴ ({mm4} mm⁴)',
  'beam.geometry.backToStandard': 'Back to Standard Sections',
  'beam.geometry.sectionClassTitle': 'Section Classification (EN 1993-1-1)',
  'beam.geometry.sectionClassHelp': 'Controls plastic/elastic resistance calculation. Auto will calculate based on geometry and yield strength.',
  'beam.geometry.classAuto': 'Auto (Calculate from geometry)',
  'beam.geometry.class1': 'Class 1 - Plastic (full plastic moment resistance)',
  'beam.geometry.class2': 'Class 2 - Compact (plastic moment, no rotation capacity)',
  'beam.geometry.class3': 'Class 3 - Semi-compact (elastic moment resistance)',
  'beam.geometry.class4': 'Class 4 - Slender (effective section required)',
  'beam.geometry.classAutoNote': 'Section class will be calculated automatically based on width-to-thickness ratios and steel grade.',
  'beam.geometry.classOverrideNote': 'Overriding automatic calculation. Using Class {n} for all checks.',
  'beam.geometry.spanLengths': 'Span Lengths (m)',
  'beam.geometry.lengthChangeHint': 'Changing length will adjust/remove loads',
  'beam.geometry.loadingCrossSection': 'Loading cross section...',
  'beam.geometry.reinforcement': 'Concrete Reinforcement',
  'beam.geometry.rebarZones': 'Rebar Zones (along beam)',
  'beam.geometry.rebarZonesHint': 'z=0 at section center; z positive to top. Cover (cnom) is to outermost reinforcement (stirrups) per EC2 4.4.1. z-positions auto-calculated from cover, stirrups Ø and bar Ø.',
  'beam.geometry.addZone': 'Add Zone',
  'beam.geometry.zoneStart': 'Start (m)',
  'beam.geometry.zoneEnd': 'End (m)',
  'beam.geometry.zoneCover': 'Cover (mm)',
  'beam.geometry.removeZone': 'Remove Zone',
  'beam.geometry.addRebarBottom': 'Add Reinforcement at bottom',
  'beam.geometry.addRebarTop': 'Add Reinforcement at top',
  'beam.geometry.addStirrups': 'Add Stirrups',
  'beam.geometry.layerZ': 'z (mm)',
  'beam.geometry.layerDiameter': 'Ø (mm)',
  'beam.geometry.layerCount': 'Count',
  'beam.geometry.remove': 'Remove',
  'beam.geometry.stirrupDiameter': 'Stirrups Ø (mm)',
  'beam.geometry.stirrupSpacing': 'Spacing (mm)',
  'beam.geometry.stirrupLegs': 'Legs',
  'beam.geometry.removeStirrups': 'Remove Stirrups',

  // ===== Beam designer — shared labels =====
  'beam.common.beamModel': 'Beam Model',

  // ===== Beam designer — materials tab =====
  'beam.materials.nationalAnnex': 'National Annex / Design Code',
  'beam.materials.annexNorwegian': '🇳🇴 Norwegian (NS-EN) - Default',
  'beam.materials.annexStandard': '🇪🇺 Standard Eurocode',
  'beam.materials.labelSteel': 'Steel',
  'beam.materials.labelConcrete': 'Concrete',
  'beam.materials.labelGlulam': 'Glued Laminated Timber',
  'beam.materials.labelSolidWood': 'Solid Wood',
  'beam.materials.labelUnknown': 'Unknown',
  'beam.materials.selectedMaterial': 'Selected Material',
  'beam.materials.changeHint': 'Change material using the selector above the tabs',
  'beam.materials.classOrGrade': 'Material Class / Grade',
  'beam.materials.steelGrade': 'Steel Grade',
  'beam.materials.subgrade': 'Subgrade',
  'beam.materials.thicknessLe': 'Thickness ≤ (mm)',
  'beam.materials.concrete': 'Concrete',
  'beam.materials.reinforcement': 'Reinforcement',
  'beam.materials.reinfNorwegian': 'Norwegian (NS 3576-3)',
  'beam.materials.reinfEurocode': 'Eurocode (EN 10080)',
  'beam.materials.b500nc': 'B500NC (ductility C)',
  'beam.materials.b500nb': 'B500NB (ductility B)',
  'beam.materials.b500c': 'B500C (ductility C)',
  'beam.materials.b500b': 'B500B (ductility B)',
  'beam.materials.b500a': 'B500A (ductility A)',
  'beam.materials.prestressingPT': 'Prestressing (PT)',
  'beam.materials.creepShrinkageParams': 'Creep & Shrinkage Parameters (EN 1992-1-1 Annex B)',
  'beam.materials.relativeHumidity': 'Relative Humidity (%)',
  'beam.materials.rh40': '40% (dry indoor)',
  'beam.materials.rh50': '50% (normal indoor)',
  'beam.materials.rh60': '60% (humid indoor)',
  'beam.materials.rh70': '70% (sheltered outdoor)',
  'beam.materials.rh80': '80% (exposed outdoor)',
  'beam.materials.rh90': '90% (very humid)',
  'beam.materials.notionalSize': 'Notional Size h₀ (mm)',
  'beam.materials.loadingAge': 'Loading Age t₀ (days)',
  'beam.materials.age7': '7 days (early)',
  'beam.materials.age14': '14 days',
  'beam.materials.age28': '28 days (standard)',
  'beam.materials.age56': '56 days',
  'beam.materials.age90': '90 days',
  'beam.materials.age365': '1 year',
  'beam.materials.cementType': 'Cement Type',
  'beam.materials.cementS': 'S (slow)',
  'beam.materials.cementN': 'N (normal)',
  'beam.materials.cementR': 'R (rapid)',
  'beam.materials.forShrinkage': 'For shrinkage',
  'beam.materials.creepCoefficient': 'Creep coefficient φ(∞,t₀):',
  'beam.materials.ecEffNote': 'Ec,eff = Ecm / (1 + φ) for long-term deflection analysis',
  'beam.materials.timberClass': 'Timber Class',
  'beam.materials.kmodLoadDuration': 'Variable-load duration class',
  'beam.materials.kmodDerivedNote': 'kmod per combination is derived from the shortest-duration action (EC5 §3.1.3). This sets the duration class of the Variable (imposed) loads; permanent/snow/wind are fixed by NA Table NA.2.2.',
  'beam.materials.durationPermanent': 'Permanent ({k})',
  'beam.materials.durationLong': 'Long-term ({k})',
  'beam.materials.durationMedium': 'Medium-term ({k})',
  'beam.materials.durationShort': 'Short-term ({k})',
  'beam.materials.durationInstant': 'Instantaneous ({k})',
  'beam.materials.kmodValue': 'kmod = {k}',
  'beam.materials.serviceClass': 'Service Class',
  'beam.materials.serviceClass1': 'Class 1 (dry, <12% MC)',
  'beam.materials.serviceClass2': 'Class 2 (protected, <20% MC)',
  'beam.materials.serviceClass3': 'Class 3 (outdoor, >20% MC)',
  'beam.materials.creepFactorKdef': 'Creep factor kdef:',
  'beam.materials.ec5Table32': '(EN 1995-1-1 Table 3.2)',
  'beam.materials.finalDeflection': 'Final deflection: u_fin = u_inst · (1 + k_def) = u_inst · {factor}',
  'beam.materials.showDetails': 'Show Details for selected material',
  'beam.materials.materialProperties': 'Material Properties',
  'beam.materials.loadingShort': '(Loading...)',
  'beam.materials.loadingFromDb': 'Loading material properties from database...',
  'beam.materials.standard': 'Standard:',
  'beam.materials.material': 'Material',
  'beam.materials.properties': 'Properties',
  'beam.materials.strengths': 'Strengths',
  'beam.materials.grade': 'Grade:',
  'beam.materials.productForm': 'Product Form:',
  'beam.materials.thicknessRange': 'Thickness Range:',
  'beam.materials.yieldStrength': 'Yield Strength (fy):',
  'beam.materials.designStrengthFyd': 'Design Strength (fyd):',
  'beam.materials.ultimateStrengthFu': 'Ultimate Strength (fu):',
  'beam.materials.elasticModulusE': 'Elastic Modulus (E):',
  'beam.materials.density': 'Density:',
  'beam.materials.type': 'Type:',
  'beam.materials.solidTimber': 'Solid Timber',
  'beam.materials.gluedLaminated': 'Glued Laminated',
  'beam.materials.class': 'Class:',
  'beam.materials.bendingFbd': 'Bending (fb,d):',
  'beam.materials.tensionFt0d': 'Tension ∥ (ft,0,d):',
  'beam.materials.compressionFc0d': 'Compression ∥ (fc,0,d):',
  'beam.materials.shearFvd': 'Shear (fv,d):',
  'beam.materials.eModulusE0': 'E-modulus (E0,mean):',
  'beam.materials.characteristicFck': 'Characteristic (fck):',
  'beam.materials.designCompressiveFcd': 'Design Compressive (fcd):',
  'beam.materials.designTensileFctd': 'Design Tensile (fctd):',
  'beam.materials.elasticModulusEcm': 'Elastic Modulus (Ecm):',
  'beam.materials.ultimateStrainEcu3': 'Ultimate Strain (εcu3):',
  'beam.materials.strainAtPeakEc3': 'Strain at Peak (εc3):',
  'beam.materials.reinforcementSteel': 'Reinforcement Steel',
  'beam.materials.ductilityClass': 'Ductility Class:',
  'beam.materials.characteristicFyk': 'Characteristic (fyk):',
  'beam.materials.tensileStrengthFtk': 'Tensile Strength (ftk):',
  'beam.materials.ratioKFtkFyk': 'Ratio (k = ftk/fyk):',
  'beam.materials.yieldStrainEyd': 'Yield Strain (εyd):',
  'beam.materials.strainAtMaxEuk': 'Strain at Max (εuk):',
  'beam.materials.prestressingSteel': 'Prestressing Steel',
  'beam.materials.tensileStrengthFpk': 'Tensile Strength (fpk):',
  'beam.materials.designStrengthFpd': 'Design Strength (fpd):',
  'beam.materials.proof01Fp01k': '0.1% Proof (fp0.1k):',
  'beam.materials.ratioKFpkFp01k': 'Ratio (k = fpk/fp0.1k):',
  'beam.materials.elasticStrainEp01k': 'Elastic Strain (εp0.1k):',
  'beam.materials.strainAtFpkEpk': 'Strain at fpk (εpk):',
  'beam.materials.ultimateStrainEuk': 'Ultimate Strain (εuk):',
  'beam.materials.relaxationClass': 'Relaxation Class:',
  'beam.materials.selfWeight': 'Self-Weight',
  'beam.materials.currentSelfWeight': 'Current self-weight load:',
  'beam.materials.selfWeightCalc': 'Calculated from: {density} kg/m³ × {area} cm² × 9.81 m/s²',
  'beam.materials.partialFactors': 'Eurocode Material Partial Factors (γM)',
  'beam.materials.checkLtb': 'Check lateral-torsional buckling (vipping)',
  'beam.materials.checkLtbHint': 'Uncheck when the construction laterally restrains the compression flange (e.g. a fixed deck) so LTB cannot occur — χLT / kcrit is then taken as 1.0.',
  'beam.materials.reliabilityClass': 'Reliability class (Pålitelighetsklasse)',
  'beam.materials.reliabilityClass1': 'RC1 — low consequence (KFI = 0.9 on variable loads)',
  'beam.materials.reliabilityClass2': 'RC2 — normal (KFI = 1.0)',
  'beam.materials.reliabilityClass3': 'RC3 — high consequence (no load-factor increase in NO NA)',
  'beam.materials.applyRc1Reduction': 'Apply KFI = 0.9 to variable loads',
  'beam.materials.applyRc1ReductionHint': 'The NA permits the reduction ("γF may be reduced"); it is not mandatory. Turn off to keep γQ = 1.5 in RC1.',
  'beam.materials.ulsSteel': 'ULS (Ultimate Limit State) - EN 1993-1-1:',
  'beam.materials.ulsConcrete': 'ULS (Ultimate Limit State) - EN 1992-1-1:',
  'beam.materials.ulsTimber': 'ULS (Ultimate Limit State) - EN 1995-1-1:',
  'beam.materials.sls': 'SLS (Serviceability Limit State):',
  'beam.materials.als': 'ALS (Accidental Limit State):',
  'beam.materials.noteCrossSectionResistance': '(resistance of cross-sections)',
  'beam.materials.noteInstabilityResistance': '(resistance to instability)',
  'beam.materials.noteTensionConnections': '(resistance of tension connections)',
  'beam.materials.noteSteelFactorsSame': 'Note: Steel material factors are the same for Norwegian and Standard Eurocode.',
  'beam.materials.noteConcrete': '(concrete)',
  'beam.materials.noteReinfSteel': '(reinforcing steel)',
  'beam.materials.notePrestressSteel': '(prestressing steel/post-tensioning)',
  'beam.materials.noteAlphaCC': '(long-term concrete strength coefficient)',
  'beam.materials.noteConcreteReduced': '(concrete - reduced)',
  'beam.materials.referenceProps': 'Reference properties:',
  'beam.materials.refReinforcement': 'Reinforcement (B500NC): fy = {fy} MPa, Es = {es} GPa',
  'beam.materials.refPrestressing': 'Prestressing (Y1860): fpk = {fpk} MPa, Ep = {ep} GPa',
  'beam.materials.noteGlulam': '(glued laminated timber)',
  'beam.materials.noteSolidSawn': '(solid/sawn timber)',
  'beam.materials.noteFromDurationService': '(from load duration and service class)',
  'beam.materials.timberDesignStrengthNote': 'Design strength: f_d = k_mod × f_k / γ_M. Glulam has a lower γM than solid timber due to its improved material quality.',
  'beam.materials.manualProperties': 'Manual Properties',
  'beam.materials.youngsModulus': "Young's Modulus (GPa):",
  'beam.materials.notesSteel': 'Steel material factors (γM0, γM1, γM2) are the same for Norwegian and Standard Eurocode.',
  'beam.materials.notesConcreteAlphaCC': 'αcc (long-term concrete strength coefficient):',
  'beam.materials.notesConcreteNorwegian': 'Norwegian NS-EN: 0.85 (15% reduction)',
  'beam.materials.notesConcreteStandard': 'Standard Eurocode: 1.00 (no reduction)',
  'beam.materials.notesConcreteOther': 'Other factors (γC, γS, γP) are the same for both.',
  'beam.materials.notesTimber': 'Timber material factors (γM) differ by national annex. Norwegian NS-EN: glulam 1.15, solid timber 1.25. Standard Eurocode: glulam 1.25, solid timber 1.30.',

  // ===== Beam designer — loads tab =====
  'beam.loads.selectSpan': 'Select Span',
  'beam.loads.imposedLoadCategory': 'Imposed-Load Category (NS-EN 1991-1-1)',
  'beam.loads.imposedLoadCategoryHint': 'Selects the ψ₀/ψ₁/ψ₂ factors for imposed loads per NS-EN 1990 Table NA.A1.1 (resolved from the codes database).',
  'beam.loads.imposedLoadCategoryA': 'A — Domestic and residential',
  'beam.loads.imposedLoadCategoryB': 'B — Offices',
  'beam.loads.imposedLoadCategoryC': 'C — Congregation areas',
  'beam.loads.imposedLoadCategoryD': 'D — Shopping areas',
  'beam.loads.imposedLoadCategoryE': 'E — Storage',
  'beam.loads.imposedLoadCategoryF': 'F — Traffic/parking, vehicles ≤ 30 kN',
  'beam.loads.imposedLoadCategoryG': 'G — Traffic/parking, vehicles 30–160 kN',
  'beam.loads.imposedLoadCategoryH': 'H — Roofs',
  'beam.loads.allSpans': 'All spans',
  'beam.loads.spanOption': 'Span {id} ({length}m)',
  'beam.loads.loadType': 'Load Type',
  'beam.loads.typeSelfWeight': 'Self-Weight (auto, kN/m)',
  'beam.loads.typeUniform': 'Uniform Load (kN/m)',
  'beam.loads.typePoint': 'Point Load (kN)',
  'beam.loads.typeTriRight': 'Triangular Load (min → max) (kN/m)',
  'beam.loads.typeTriLeft': 'Triangular Load (max → min) (kN/m)',
  'beam.loads.typeAxial': 'Axial Force (at span end, kN)',
  'beam.loads.loadCategory': 'Load Category',
  'beam.loads.catPermanent': 'Permanent (G)',
  'beam.loads.catVariable': 'Variable (Q)',
  'beam.loads.catSnow': 'Snow (Qs)',
  'beam.loads.catWind': 'Wind (Qw)',
  'beam.loads.badgePermanent': 'Permanent',
  'beam.loads.badgeVariable': 'Variable',
  'beam.loads.badgeSnow': 'Snow',
  'beam.loads.badgeWind': 'Wind',
  'beam.loads.badgePrestress': 'Prestress',
  'beam.loads.shortUniform': 'Uniform Load',
  'beam.loads.shortPoint': 'Point Load',
  'beam.loads.shortTriRight': 'Triangular Load (min → max)',
  'beam.loads.shortTriLeft': 'Triangular Load (max → min)',
  'beam.loads.shortAxial': 'Axial Force',
  'beam.loads.dirDown': '(downward)',
  'beam.loads.dirUp': '(upward)',
  'beam.loads.dirUpDown': '(upward and downward)',
  'beam.loads.tension': '(tension)',
  'beam.loads.compression': '(compression)',
  'beam.loads.endLeft': 'left',
  'beam.loads.endRight': 'right',
  'beam.loads.uniformDesc': '{intensity} kN/m {dir}',
  'beam.loads.pointDesc': '{load} kN @ {pos}m {dir}',
  'beam.loads.triangularDesc': '{start} → {end} kN/m ({startPos}m to {endPos}m) {dir}',
  'beam.loads.axialDesc': '{force} kN at {end} end {dir}{ecc}',
  'beam.loads.axialEcc': ', e = {e} m (M = {m} kNm)',
  'beam.loads.noLoads': 'No loads applied to this span',
  'beam.loads.selfWeightAutoTitle': 'Self-weight (automatic)',
  'beam.loads.selfWeightUpdates': 'Updates automatically with material/section changes',
  'beam.loads.editLoad': 'Edit load',
  'beam.loads.removeSelfWeight': 'Remove self-weight load',
  'beam.loads.removeLoad': 'Remove load',
  'beam.loads.appliedLoads': 'Applied Loads on Span {span}',
  'beam.loads.appliedLoadsAll': 'Applied Loads (all spans)',
  'beam.loads.selfWeightFull': 'Self-Weight (kN/m, auto from material/section)',
  'beam.loads.loadValueKnm': 'Load Value (kN/m)',
  'beam.loads.loadValueKn': 'Load Value (kN):',
  'beam.loads.position': 'Position (m):',
  'beam.loads.minValue': 'Min Value',
  'beam.loads.maxValue': 'Max Value',
  'beam.loads.startPosition': 'Start Position (m):',
  'beam.loads.endPosition': 'End Position (m):',
  'beam.loads.forceValue': 'Force Value (kN):',
  'beam.loads.posTensionNegCompression': 'Positive = Tension, Negative = Compression',
  'beam.loads.eccentricity': 'Eccentricity (m):',
  'beam.loads.eccentricityHint': 'Distance from centroid (creates moment M = N × e)',
  'beam.loads.applyAt': 'Apply At:',
  'beam.loads.leftEnd': 'Left End',
  'beam.loads.rightEnd': 'Right End',
  'beam.loads.saveChanges': 'Save Changes',
  'beam.loads.addLoad': 'Add Load',
  'beam.loads.cancel': 'Cancel',
  'beam.loads.beamWithLoads': 'Beam with Loads',

  // ----- PT cables (concrete only) -----
  'beam.pt.sectionAria': 'PT Cables Post-Tensioning section',
  'beam.pt.title': 'PT Cables (Post-Tensioning)',
  'beam.pt.materialNotLoaded': 'Material properties not fully loaded. Loss calculations may be incomplete.',
  'beam.pt.intro': 'Define post-tensioning cables using strand count and stress level. Losses are calculated per EC2 5.10.',
  'beam.pt.appliedCables': 'Applied PT Cables',
  'beam.pt.removeCable': 'Remove cable',
  'beam.pt.addCable': 'Add PT Cable',
  'beam.pt.strandType': 'Strand Type',
  'beam.pt.numberOfStrands': 'Number of Strands',
  'beam.pt.strandArea': 'Strand Area (mm²)',
  'beam.pt.totalArea': 'Total: {area} mm²',
  'beam.pt.initialStressLevel': 'Initial Stress Level (%)',
  'beam.pt.percentOfMax': '% of max ({max} MPa)',
  'beam.pt.initialStress': 'Initial Stress σp0',
  'beam.pt.ec2Limit': 'EC2 5.10.3 limit',
  'beam.pt.initialForce': 'Initial Force P₀',
  'beam.pt.apSigma': '= Ap × σp0',
  'beam.pt.lossParameters': 'Loss Parameters (EC2 5.10.5-6)',
  'beam.pt.frictionMu': 'Friction μ',
  'beam.pt.typ019': 'Typ. 0.19-0.25',
  'beam.pt.wobbleK': 'Wobble k (1/m)',
  'beam.pt.typ0005': 'Typ. 0.005-0.01',
  'beam.pt.anchorageSlip': 'Anchorage Slip (mm)',
  'beam.pt.typ6mm': 'Typ. 6 mm',
  'beam.pt.bondType': 'Bond Type',
  'beam.pt.unbonded': 'Unbonded',
  'beam.pt.grouted': 'Grouted (bonded)',
  'beam.pt.groutedLabel': ' [Grouted]',
  'beam.pt.prestressEffect': 'Prestress Effect (EC2 2.4.2.2)',
  'beam.pt.favorableOption': 'Favorable (γP = 1.0) - Prestress reduces effects',
  'beam.pt.unfavorableOption': 'Unfavorable (γP = 1.2) - Prestress increases effects',
  'beam.pt.effectHint': 'Select based on whether prestress helps or hinders the design check',
  'beam.pt.favorableLabel': 'Favorable:',
  'beam.pt.favorableText': 'Prestress counteracts gravity (typical for sagging moment).',
  'beam.pt.unfavorableLabel': 'Unfavorable:',
  'beam.pt.unfavorableText': 'Prestress adds to effects (e.g., hogging over supports).',
  'beam.pt.cableProfile': 'Cable Profile',
  'beam.pt.endSupportEcc': 'End Support Ecc. (m)',
  'beam.pt.typically0': 'Typically 0',
  'beam.pt.interiorSupportEcc': 'Interior Support Ecc. (m)',
  'beam.pt.aboveNA': 'Above NA (max: {max}m)',
  'beam.pt.midSpanEcc': 'Mid-Span Ecc. (m)',
  'beam.pt.belowNA': 'Below NA (max: {max}m)',
  'beam.pt.calculating': 'Calculating...',
  'beam.pt.calculatingLosses': 'Calculating losses...',
  'beam.pt.failedToCalculate': 'Failed to calculate losses',
  'beam.pt.lossPreview': 'Prestress Loss Preview (EC2)',
  'beam.pt.immediateLosses': 'Immediate Losses',
  'beam.pt.friction': 'Friction (5.10.5.2):',
  'beam.pt.anchorageSlipLoss': 'Anchorage slip (5.10.5.3):',
  'beam.pt.elasticShortening': 'Elastic shortening (5.10.5.1):',
  'beam.pt.subtotal': 'Subtotal:',
  'beam.pt.timeDependent': 'Time-Dependent (50 yr)',
  'beam.pt.relaxation': 'Relaxation (3.3.2):',
  'beam.pt.creep': 'Creep (5.10.6):',
  'beam.pt.shrinkage': 'Shrinkage (5.10.6):',
  'beam.pt.initialForceShort': 'Initial Force',
  'beam.pt.totalLoss': 'Total Loss',
  'beam.pt.effectiveForce': 'Effective Force',
  'beam.pt.addCableAria': 'Add PT Cable to beam',
  'beam.pt.singleSpanNote': 'For single-span beams, the cable profile is parabolic from end to end.',

  // ===== Beam designer — results tab =====
  'beam.results.catVariable': 'Variable (Q)',
  'beam.results.catSnow': 'Snow',
  'beam.results.catWind': 'Wind',
  'beam.results.catPermanent': 'Permanent (G)',
  'beam.results.catEnvelope': 'Envelope',
  'beam.results.noVariableLeading': 'No variable leading',
  'beam.results.leading': 'Leading: {category}',
  'beam.results.multiLimitState': 'Multi-limit-state analysis',
  'beam.results.beamTheory': 'Beam Theory',
  'beam.results.analyzing': 'Analyzing...',
  'beam.results.timoshenkoTitle': 'Includes shear deformation - recommended for deep beams (L/h < 10)',
  'beam.results.eulerDesc': 'Standard theory for slender beams (L/h > 20)',
  'beam.results.timoshenkoDesc': 'Includes shear deformation - for deep beams (L/h < 10)',
  'beam.results.timberAlwaysTimoshenko': 'Timber always runs Timoshenko: shear deformation matters for timber (EN 1995-1-1 §2.2.3(2)), so both buttons use the shear-inclusive analysis.',
  'beam.results.yourBeam': 'Your beam: L/h = {ratio}',
  'beam.results.slenderNote': ' (slender — Euler-Bernoulli is appropriate)',
  'beam.results.intermediateNote': ' (intermediate — consider Timoshenko)',
  'beam.results.deepNote': ' (deep beam — Timoshenko recommended)',
  'beam.results.analysisCompleted': '{theory} analysis completed - Select limit state below',
  'beam.results.selectTheoryHint': 'Select a beam theory and click to analyze',
  'beam.results.limitState': 'Limit State',
  'beam.results.ulsOption': 'Ultimate (ULS)',
  'beam.results.slsCharacteristic': 'Serviceability - Characteristic',
  'beam.results.slsFrequent': 'Serviceability - Frequent',
  'beam.results.slsQuasi': 'Serviceability - Quasi-permanent',
  'beam.results.runFirstLimitStates': 'Run analysis first to select limit states',
  'beam.results.loadCombinations': 'Load Combinations',
  'beam.results.loadCaseFactors': 'Load Case Factors',
  'beam.results.thCategory': 'Category',
  'beam.results.thLeading': 'Leading',
  'beam.results.thApplied': 'Applied',
  'beam.results.yes': 'Yes',
  'beam.results.no': 'No',
  'beam.results.envelopeNotice': 'Envelope shown – values represent the maximum response across all load combinations.',
  'beam.results.diagramType': 'Diagram Type',
  'beam.results.displayOptions': 'Display Options',
  'beam.results.showValues': 'Show Values',
  'beam.results.highlightMaxMin': 'Highlight Max/Min',
  'beam.results.valuePrecision': 'Value precision',
  'beam.results.dp2': '2 dp',
  'beam.results.dp4': '4 dp',
  'beam.results.fontSize': 'Font size',
  'beam.results.diagramScale': 'Diagram Scale',
  'beam.results.valueDensity': 'Value Density',
  'beam.results.everyNPts': 'Every {n} pts',
  'beam.results.valueDensityHint': 'Controls how many numerical values are shown at key locations',
  'beam.results.runFirstDiagrams': 'Run analysis first to view results diagrams.',
  'beam.results.analysisComplete': 'Analysis complete! View diagrams in the visualization panel.',
  'beam.results.analysisFailedMsg': 'Analysis failed. Please check your beam configuration.',
  'beam.results.cardMomentTitle': 'Moment',
  'beam.results.cardMomentDesc': 'Bending Moment Diagram',
  'beam.results.cardShearTitle': 'Shear',
  'beam.results.cardShearDesc': 'Shear Force Diagram',
  'beam.results.cardDeflectionTitle': 'Deflection',
  'beam.results.cardDeflectionDesc': 'Displacement Curve',
  'beam.results.cardAxialTitle': 'Axial',
  'beam.results.cardAxialDesc': 'Axial Force Diagram',
  'beam.results.cardReactionsTitle': 'Reactions',
  'beam.results.cardReactionsDesc': 'Support Reactions',
  'beam.results.cardAxialDispTitle': 'Axial Disp.',
  'beam.results.cardAxialDispDesc': 'Axial Displacement (mm)',
  'beam.results.labelMoment': 'Moment Diagram',
  'beam.results.labelShear': 'Shear Diagram',
  'beam.results.labelDeflection': 'Deflection Curve',
  'beam.results.labelAxial': 'Axial Force Diagram',
  'beam.results.labelAxialDisp': 'Axial Displacement',
  'beam.results.labelReactions': 'Support Reactions',
  'beam.results.badgeUls': 'ULS Combination',
  'beam.results.badgeSlsChar': 'SLS - Characteristic',
  'beam.results.badgeSlsFreq': 'SLS - Frequent',
  'beam.results.badgeSlsQuasi': 'SLS - Quasi-permanent',
  'beam.results.dataAvailable': 'Data Available',

  // ===== Beam designer — response tab =====
  'beam.response.calcRunning': 'Calculating Response...',
  'beam.response.calc': 'Calculate Response',
  'beam.response.calcHint': 'Click to calculate strains, stresses & URs from analysis results',
  'beam.response.dataLoaded': 'Response data loaded',
  'beam.response.urUls': 'Utilization Ratios (URs) - ULS',
  'beam.response.urSls': 'Utilization Ratios (URs) - SLS',
  'beam.response.showDetailsCombo': 'Show Details for selected Load Combination',
  'beam.response.noUlsCombos': 'No ULS combinations',
  'beam.response.noSlsCombos': 'No SLS combinations',
  'beam.response.strainsStresses': 'Strains & Stresses',
  'beam.response.strainStressInfo': 'Based on linear elastic stiffness (E·I) — for information only. Reasonably accurate for steel and timber, but does not reflect cracked or plastic behaviour in concrete sections.',
  'beam.response.stresses': 'Stresses',
  'beam.response.strains': 'Strains',
  'beam.response.urMaxOverallTitle': 'Max Overall UR',
  'beam.response.urMaxOverallDesc': 'Overall maximum',
  'beam.response.urBendingTitle': 'Bending UR',
  'beam.response.urBendingDesc': 'Bending capacity',
  'beam.response.urShearTitle': 'Shear UR',
  'beam.response.urShearDesc': 'Shear capacity',
  'beam.response.urCompressionTitle': 'Compression UR',
  'beam.response.urCompressionDesc': 'Axial compression',
  'beam.response.urTensionTitle': 'Tension UR',
  'beam.response.urTensionDesc': 'Axial tension',
  'beam.response.slsDeflectionTitle': 'Deflection UR',
  'beam.response.slsDeflectionDesc': 'Deflection limits',
  'beam.response.slsDeflectionInstTitle': 'Deflection w_inst',
  'beam.response.slsDeflectionInstDesc': 'Instantaneous (characteristic)',
  'beam.response.slsDeflectionFinalTitle': 'Deflection w_fin',
  'beam.response.slsDeflectionFinalDesc': 'Final incl. creep',
  'beam.response.slsDeflectionFinalDescSteel': 'Final – steel does not creep, w_fin = w_inst',
  'beam.response.slsCrackWidthTitle': 'SLS Crack Width',
  'beam.response.slsCrackWidthDesc': 'Concrete crack width',
  'beam.response.slsConcreteStressTitle': 'SLS Concrete Stress',
  'beam.response.slsConcreteStressDesc': 'Concrete stress limit',
  'beam.response.slsSteelStressTitle': 'SLS Steel Stress',
  'beam.response.slsSteelStressDesc': 'Steel/Rebar stress',
  'beam.response.slsTimberStressTitle': 'Timber Stress UR',
  'beam.response.slsTimberStressDesc': 'Timber stress limit',
  'beam.response.nameEpsilonTop': 'Strain at Top Fiber (ε Top)',
  'beam.response.nameEpsilonMid': 'Strain at Mid-depth (ε Mid)',
  'beam.response.nameEpsilonBot': 'Strain at Bottom Fiber (ε Bot)',
  'beam.response.nameSigmaTop': 'Normal Stress at Top (σ Top)',
  'beam.response.nameSigmaMid': 'Normal Stress at Mid (σ Mid)',
  'beam.response.nameSigmaBot': 'Normal Stress at Bottom (σ Bot)',
  'beam.response.nameGammaLower': 'Shear Strain Lower (γ Lower)',
  'beam.response.nameGammaMid': 'Shear Strain Mid (γ Mid)',
  'beam.response.nameGammaUpper': 'Shear Strain Upper (γ Upper)',
  'beam.response.nameTauLower': 'Shear Stress Lower (τ Lower)',
  'beam.response.nameTauMid': 'Shear Stress Mid (τ Mid)',
  'beam.response.nameTauUpper': 'Shear Stress Upper (τ Upper)',
  'beam.response.nameUrMaxOverall': 'Maximum Overall Utilization Ratio',
  'beam.response.nameUrBending': 'Bending Utilization Ratio',
  'beam.response.nameUrCompression': 'Compression Utilization Ratio',
  'beam.response.nameUrTension': 'Tension Utilization Ratio',
  'beam.response.nameUrShear': 'Shear Utilization Ratio',
  'beam.response.nameUrSlsDeflection': 'SLS Deflection Utilization Ratio',
  'beam.response.nameUrSlsCrackWidth': 'SLS Crack Width Utilization Ratio (Concrete)',
  'beam.response.nameUrSlsConcreteStress': 'SLS Concrete Stress Utilization Ratio',
  'beam.response.nameUrSlsSteelStress': 'SLS Steel/Rebar Stress Utilization Ratio',
  'beam.response.nameSlsDeflectionInst': 'SLS Instantaneous Deflection w_inst (characteristic combination)',
  'beam.response.nameSlsDeflectionFinal': 'SLS Final Deflection w_fin incl. creep (characteristic combination)',
  'beam.response.nameSlsDeflectionFinalSteel': 'SLS Final Deflection w_fin = w_inst – steel has no creep (characteristic combination)',
  'beam.response.nameDefault': 'Response Diagram',
  'beam.response.codeCheck.title': 'Code Check – Documentation',
  'beam.response.codeCheck.check': 'Check',
  'beam.response.codeCheck.explanation': 'Explanation',
  'beam.response.codeCheck.formula': 'Formula and values',
  'beam.response.codeCheck.ur': 'UR',
  'beam.response.codeCheck.na': 'N/A',
  'beam.response.noFactorDetails': 'No factor details available.',
  'beam.response.thLoadCategory': 'Load category',
  'beam.response.badgeLeading': 'Leading',
  'beam.response.leadingNoColon': 'Leading {category}',
  'beam.response.uls': 'ULS',
  'beam.response.evaluatedFor': 'Evaluated for: {combination}',
  'beam.response.checkNotEvaluated': 'This check was not evaluated for any load combination in this analysis (the limit is not configured, or the check does not apply to the material).',

  // ===== Beam designer — dashboard =====
  'beam.dashboard.greetingMorning': 'Good morning',
  'beam.dashboard.greetingAfternoon': 'Good afternoon',
  'beam.dashboard.greetingEvening': 'Good evening',
  'beam.dashboard.defaultName': 'Designer',
  'beam.dashboard.welcomeTitle': '{greeting}, {name}',
  'beam.dashboard.readyToDesign': 'Ready to Design',
  'beam.dashboard.welcomeSubtitle': 'Start a new beam analysis or continue with a template below',
  'beam.dashboard.newBlankDesign': 'New Blank Design',
  'beam.dashboard.newBlankDesc': 'Start from scratch with full control',
  'beam.dashboard.myAccount': 'My Account',
  'beam.dashboard.myAccountDesc': 'Manage subscription and profile',
  'beam.dashboard.beamTemplates': 'Beam Templates',
  'beam.dashboard.beamTemplatesDesc': 'Pre-configured beam designs for common structural scenarios. Select a template to get started quickly.',
  'beam.dashboard.materialConcrete': 'Concrete',
  'beam.dashboard.materialSteel': 'Steel',
  'beam.dashboard.materialTimber': 'Timber',
  'beam.dashboard.whyChoose': 'Why Choose StructuraMatrix Beam Designer?',
  'beam.dashboard.benefitMultiSpanLabel': 'Multi-Span Analysis:',
  'beam.dashboard.benefitMultiSpanDesc': 'Continuous beams with unlimited spans',
  'beam.dashboard.benefitEurocodeLabel': 'Eurocode Compliance:',
  'beam.dashboard.benefitEurocodeDesc': 'Steel, concrete, and timber code checking',
  'beam.dashboard.benefitVisualizationLabel': 'Interactive Visualization:',
  'beam.dashboard.benefitVisualizationDesc': 'Real-time results with professional diagrams',
  'beam.dashboard.benefitResultsLabel': 'Professional Results:',
  'beam.dashboard.benefitResultsDesc': 'Detailed engineering calculations and reports',
  'beam.dashboard.technicalFeatures': 'Technical Features',
  'beam.dashboard.featureLoadTypes': 'Multiple load types (point, distributed, triangular)',
  'beam.dashboard.featureDiagrams': 'Moment, shear, and deflection diagrams',
  'beam.dashboard.featureReactions': 'Support reactions and utilization ratios',
  'beam.dashboard.featureLoadCases': 'Load case management and combinations',
  'beam.dashboard.useTemplate': 'Use Template',
  'beam.dashboard.spansSingular': '{count} span ({lengths}m)',
  'beam.dashboard.spansPlural': '{count} spans ({lengths}m)',
  'beam.dashboard.solidSuffix': ' (Solid)',
  'beam.dashboard.glulamSuffix': ' (Glulam)',
  // Concrete templates
  'beam.dashboard.tpl.concrete-residential.name': 'Residential Floor',
  'beam.dashboard.tpl.concrete-residential.description': 'Single-span beam for residential floor systems in apartments and houses.',
  'beam.dashboard.tpl.concrete-residential.load': '5 kN/m² live + finishes',
  'beam.dashboard.tpl.concrete-office.name': 'Office Continuous',
  'beam.dashboard.tpl.concrete-office.description': 'Two-span continuous beam for office buildings with moderate loading.',
  'beam.dashboard.tpl.concrete-office.load': '3 kN/m² live + partitions',
  'beam.dashboard.tpl.concrete-parking.name': 'Parking Structure',
  'beam.dashboard.tpl.concrete-parking.description': 'Three-span continuous beam for multi-story parking decks.',
  'beam.dashboard.tpl.concrete-parking.load': '2.5 kN/m² vehicles',
  'beam.dashboard.tpl.concrete-industrial.name': 'Industrial Floor',
  'beam.dashboard.tpl.concrete-industrial.description': 'Heavy-duty continuous beam for industrial floors with equipment loads.',
  'beam.dashboard.tpl.concrete-industrial.load': '15 kN/m² machinery',
  'beam.dashboard.tpl.concrete-transfer.name': 'Transfer Beam',
  'beam.dashboard.tpl.concrete-transfer.description': 'Deep transfer beam supporting columns above. Heavy point loads.',
  'beam.dashboard.tpl.concrete-transfer.load': 'Column loads 500+ kN',
  // Steel templates
  'beam.dashboard.tpl.steel-industrial.name': 'Industrial Beam',
  'beam.dashboard.tpl.steel-industrial.description': 'Heavy-duty single-span beam for warehouses and industrial buildings.',
  'beam.dashboard.tpl.steel-industrial.load': '10 kN/m² storage',
  'beam.dashboard.tpl.steel-bridge.name': 'Pedestrian Bridge',
  'beam.dashboard.tpl.steel-bridge.description': 'Single-span girder for pedestrian bridges and elevated walkways.',
  'beam.dashboard.tpl.steel-bridge.load': '5 kN/m² pedestrian',
  'beam.dashboard.tpl.steel-mezzanine.name': 'Mezzanine Floor',
  'beam.dashboard.tpl.steel-mezzanine.description': 'Two-span continuous beam for office mezzanine or intermediate floors.',
  'beam.dashboard.tpl.steel-mezzanine.load': '5 kN/m² office',
  'beam.dashboard.tpl.steel-roof-purlin.name': 'Roof Purlin',
  'beam.dashboard.tpl.steel-roof-purlin.description': 'Light steel purlin for industrial roof cladding support.',
  'beam.dashboard.tpl.steel-roof-purlin.load': 'Snow + cladding',
  'beam.dashboard.tpl.steel-platform.name': 'Platform Beam',
  'beam.dashboard.tpl.steel-platform.description': 'Industrial platform or walkway beam with handrail and grating loads.',
  'beam.dashboard.tpl.steel-platform.load': '3 kN/m² platform',
  // Timber templates
  'beam.dashboard.tpl.timber-roof-simple.name': 'Roof Beam Simple',
  'beam.dashboard.tpl.timber-roof-simple.description': '10.0 m laterally restrained glulam roof beam with roof build-up, snow, and wind.',
  'beam.dashboard.tpl.timber-roof-simple.load': 'Gk 0.6 + sk 2.0 kN/m² · 3.0 m tributary width',
  'beam.dashboard.tpl.timber-roof-overhang.name': 'Roof with Overhang',
  'beam.dashboard.tpl.timber-roof-overhang.description': '8.0 m laterally restrained glulam roof beam with a true 1.5 m cantilevered eave and increased snow load on the overhang.',
  'beam.dashboard.tpl.timber-roof-overhang.load': 'Roof + snow 4.5/7.0 kN/m + wind',
  'beam.dashboard.tpl.timber-floor-multispan.name': 'Floor Multi-Span',
  'beam.dashboard.tpl.timber-floor-multispan.description': 'Three-span continuous C24 floor joist at 600 mm centres, laterally restrained by the floor.',
  'beam.dashboard.tpl.timber-floor-multispan.load': 'Gk 0.75 + qk 2.0 kN/m² · 600 mm centres',
  'beam.dashboard.tpl.timber-floor-joist.name': 'Floor Joist',
  'beam.dashboard.tpl.timber-floor-joist.description': '3.6 m C24 floor joist, 48×223 mm at 600 mm centres, laterally restrained by the floor.',
  'beam.dashboard.tpl.timber-floor-joist.load': 'Gk 0.75 + qk 2.0 kN/m² · 600 mm centres',
  'beam.dashboard.tpl.timber-carport.name': 'Carport / Canopy',
  'beam.dashboard.tpl.timber-carport.description': '6.0 m roof-restrained glulam carport beam with true 1.0 m cantilevers at both ends.',
  'beam.dashboard.tpl.timber-carport.load': 'Light roof + snow 4.0 kN/m + wind',
  'beam.dashboard.tpl.timber-ridge-beam.name': 'Ridge Beam',
  'beam.dashboard.tpl.timber-ridge-beam.description': '7.5 m laterally restrained glulam beam supporting rafters at the ridge of a pitched roof.',
  'beam.dashboard.tpl.timber-ridge-beam.load': 'Gk 0.5 + sk 1.5 kN/m² · 4.0 m tributary width',
  'beam.dashboard.load.selfWeight': 'G1 – Self-weight',
  'beam.dashboard.load.roofBuildUp': 'G2 – Roof build-up',
  'beam.dashboard.load.lightRoofBuildUp': 'G2 – Light roof build-up',
  'beam.dashboard.load.floorBuildUp': 'G2 – Floor build-up',
  'beam.dashboard.load.residential': 'Q – Residential imposed load, category A',
  'beam.dashboard.load.snow': 'S – Snow load',
  'beam.dashboard.load.snowWithDrift': 'S – Snow load with drift',
  'beam.dashboard.load.windPressure': 'W – Downward wind pressure',
  'beam.dashboard.timberTemplateAssumptions': 'Examples use illustrative characteristic actions and assumed tributary widths. Always verify site snow and wind, actual tributary width, lateral restraint, and connections before design.',

  // ===== Beam designer — report tab =====
  'beam.report.title': 'Calculation Report',
  'beam.report.generateNew': 'Generate New Report',
  'beam.report.completeAnalysisFirst': 'Please complete the analysis in the Response & URs tab before generating a report.',
  'beam.report.analysisCompleteCanGenerate': 'Analysis complete. You can now generate a calculation report.',
  'beam.report.previousReports': 'Previous Reports',
  'beam.report.noReports': 'No reports generated yet.',
  'beam.report.thProjectName': 'Project Name',
  'beam.report.thProjectId': 'Project ID',
  'beam.report.thBeamType': 'Beam Type',
  'beam.report.thGenerated': 'Generated',
  'beam.report.thVerifiedBy': 'Verified By',
  'beam.report.thActions': 'Actions',
  'beam.report.verifyThis': 'Verify this report',
  'beam.report.verify': 'Verify',
  'beam.report.statusVerified': 'VERIFIED',
  'beam.report.statusNotApproved': 'NOT APPROVED',
  'beam.report.downloadPdf': 'Download PDF',
  'beam.report.delete': 'Delete',
  'beam.report.confirmDelete': 'Are you sure you want to delete this report?',
  'beam.report.errLoadList': 'Failed to load report list',
  'beam.report.errLoading': 'Error loading reports: {message}',
  'beam.report.errResultRequired': 'Analysis result is required. Please run analysis first.',
  'beam.report.errNoAnalysisId': 'Analysis ID not found. Please run analysis again.',
  'beam.report.errGenerate': 'Failed to generate report',
  'beam.report.errGenerating': 'Error generating report: {message}',
  'beam.report.errDownload': 'Failed to download report',
  'beam.report.errDownloading': 'Error downloading report: {message}',
  'beam.report.errDelete': 'Failed to delete report',
  'beam.report.errDeleting': 'Error deleting report: {message}',
  'beam.report.errVerify': 'Failed to verify report',
  'beam.report.errVerifying': 'Error verifying report: {message}',

  // ----- Report settings dialog -----
  'beam.report.settingsTitle': 'Generate Calculation Report',
  'beam.report.projectName': 'Project Name',
  'beam.report.projectNamePlaceholder': 'Beam Design',
  'beam.report.projectId': 'Project ID',
  'beam.report.autoGenerated': 'Auto-generated',
  'beam.report.designerName': 'Designer Name',
  'beam.report.designerPlaceholder': 'Enter designer name',
  'beam.report.companyName': 'Company Name',
  'beam.report.companyPlaceholder': 'Enter company name',
  'beam.report.projectDescription': 'Project Description',
  'beam.report.descriptionPlaceholder': 'Brief description of the project and structure',
  'beam.report.appearsSection1': 'Will appear in Section 1 of the report',
  'beam.report.purpose': 'Purpose',
  'beam.report.purposePlaceholder': 'Purpose of this calculation and intended use',
  'beam.report.noteLabel': 'Note:',
  'beam.report.noteText': 'The report will be generated as a PDF document and automatically downloaded to your device. The file includes complete analysis results, design calculations, and detailed diagrams.',
  'beam.report.generating': 'Generating...',
  'beam.report.generateReport': 'Generate Report',

  // ----- Verification dialog -----
  'beam.report.verifyTitle': 'Verify Calculation Report',
  'beam.report.verificationStatus': 'Verification Status',
  'beam.report.approved': 'Approved',
  'beam.report.notApproved': 'Not Approved',
  'beam.report.companyNameRequired': 'Company Name *',
  'beam.report.companyLogo': 'Company Logo (optional)',
  'beam.report.logoUploaded': 'Logo uploaded',
  'beam.report.verifierNameRequired': 'Verifier Name *',
  'beam.report.verifierPlaceholder': 'Enter name of person responsible',
  'beam.report.signature': 'Signature (optional)',
  'beam.report.signatureUploaded': 'Signature uploaded',
  'beam.report.commentsRequired': 'Comments *',
  'beam.report.commentsPlaceholder': 'Enter verification comments',
  'beam.report.verifying': 'Verifying...',
  'beam.report.submitVerification': 'Submit Verification',

  // ===== Beam designer — cross-section details table =====
  'beam.section.section': 'Section:',
  'beam.section.material': 'Material:',
  'beam.section.width': 'Width:',
  'beam.section.height': 'Height:',
  'beam.section.area': 'Area:',
  'beam.section.inertia': 'Inertia',
  'beam.section.approx': 'Approx',
  'beam.section.webT': 'Web t:',
  'beam.section.flangeT': 'Flange t:',
  'beam.section.sectionClass': 'Section Class:',
  'beam.section.shearFactor': 'κ (shear factor):',
  'beam.section.reinforcement': 'reinforcement',
  'beam.section.effectiveDepth': 'effective depth',
  'beam.section.reinforcementRatio': 'reinforcement ratio',
  'beam.section.shearReinforcement': 'shear reinforcement',
  'beam.section.plasticModulusUnavailable': 'Plastic modulus unavailable',
  'beam.section.classShort1': 'Class 1 - Plastic',
  'beam.section.classShort2': 'Class 2 - Compact',
  'beam.section.classShort3': 'Class 3 - Semi-compact',
  'beam.section.classShort4': 'Class 4 - Slender',
  'beam.section.classShortN': 'Class {n}',
  'beam.section.classAutoGeometry': 'Auto (calculated from geometry)',
  'beam.section.autoSuffix': ' (auto)',

  // ===== Beam designer — visualization controls & misc =====
  'beam.viz.fitAll': 'Fit All',
  'beam.viz.resetView': 'Reset View',
  'beam.viz.dragToResize': 'Drag to resize',
  'beam.viz.noCrossSection': 'No cross-section defined',
  'beam.viz.strain': 'Strain ε',
  'beam.viz.stress': 'Stress σ',
  'beam.viz.utilization': 'Utilization',
  'beam.common.unknownTab': 'Unknown tab',
  'beam.common.close': 'Close',

  // ===== Beam designer — buckling details modal =====
  'beam.buckling.title': 'Second-Order Effects - Buckling Analysis',
  'beam.buckling.resultScope': 'Result Scope:',
  'beam.buckling.globalOverall': 'Global / Overall Beam',
  'beam.buckling.spanOption': 'Span {n} (L = {length} m)',
  'beam.buckling.globalSummary': 'Global Buckling Summary',
  'beam.buckling.spanSummary': 'Span {n} Buckling Summary',
  'beam.buckling.criticalMode': 'Critical Mode:',
  'beam.buckling.none': 'None',
  'beam.buckling.maxUtilization': 'Max Utilization:',
  'beam.buckling.status': 'Status:',
  'beam.buckling.pass': '✓ PASS',
  'beam.buckling.fail': '✗ FAIL',
  'beam.buckling.mnDiagram': 'M-N Interaction Diagram',
  'beam.buckling.axialForce': 'Axial Force',
  'beam.buckling.moment': 'Moment',
  'beam.buckling.axialForceAxis': 'Axial Force N [kN]',
  'beam.buckling.momentAxis': 'Moment M [kN⋅m]',
  'beam.buckling.secondOrderResistance': 'Second-order resistance',
  'beam.buckling.designPoint': 'Design point',
  'beam.buckling.mnNote': 'This is a simplified linear interaction curve. Actual resistance curves may be non-linear depending on material and cross-section properties.',
  'beam.buckling.modesSummary': 'Buckling Modes Summary',
  'beam.buckling.thMode': 'Mode',
  'beam.buckling.thDescription': 'Description',
  'beam.buckling.thStatus': 'Status',
  'beam.buckling.noModes': 'No buckling modes calculated for this selection.',
  'beam.buckling.legendLabel': 'Legend:',
  'beam.buckling.legendText': 'L_eff = Effective length, λ = Slenderness, χ = Reduction factor, UR = Utilization Ratio',
  'beam.buckling.note': 'Note:',

  // ===== Beam designer — dynamics details modal =====
  // Frequency SCREENING only — the modal must never present a code PASS/FAIL: the EN 1995-1-1
  // §7.3.3 floor verification is not performed (see backend DynamicAnalysisResult).
  'beam.dynamics.title': 'Dynamic Behavior - Vibration Estimate',
  'beam.dynamics.noResults': 'No dynamics results available. Please calculate dynamics first.',
  'beam.dynamics.summary': 'Frequency Estimate Summary (Global System, permanent-mass gravity case)',
  'beam.dynamics.fundamentalFreq': 'Fundamental Frequency (f₁):',
  'beam.dynamics.screeningThreshold': 'Screening threshold:',
  'beam.dynamics.aboveScreening': 'f₁ is above the screening threshold. Screening only — the full vibration verification is not performed.',
  'beam.dynamics.belowScreening': 'f₁ is at or below the screening threshold — a special investigation is required (EN 1995-1-1 §7.3.3(1)).',
  'beam.dynamics.reference': 'Reference:',
  'beam.dynamics.naturalFrequencies': 'Natural Frequencies (estimates)',
  'beam.dynamics.modeNumber': 'Mode Number',
  'beam.dynamics.frequencyHz': 'Frequency [Hz]',
  'beam.dynamics.tooltipFrequency': 'Frequency: {value} Hz',
  'beam.dynamics.tooltipPeriod': 'Period: {value} s',
  'beam.dynamics.continuousNote': 'Note: For continuous beams, these frequencies represent the global system behavior. Mode 1 uses the permanent-mass gravity deformation (Rayleigh); higher modes are simply-supported approximations.',
  'beam.dynamics.timberFloorEstimates': 'Timber Floor Vibration Estimates (informational)',
  'beam.dynamics.fundamentalFreqRow': 'Fundamental frequency:',
  'beam.dynamics.unitImpulseVelocity': 'Impulse velocity estimate:',
  'beam.dynamics.modalMass': 'Permanent (vibrating) mass:',
  'beam.dynamics.dampingRatio': 'Assumed damping ratio:',
  'beam.dynamics.timberEstimateNote': 'These numbers are rough estimates only. The EN 1995-1-1 §7.3.3 criteria (expressions (7.3)-(7.7) with the NA.7.3.3 parameters) are not evaluated.',

  // ===== Pages — shared =====
  'pages.logoAlt': 'StructuraMatrix Logo',

  // ----- Home page -----
  'pages.home.heroTitle': 'Professional Structural Analysis Services',
  'pages.home.heroSubtitle': 'Advanced continuous beam analysis with interactive visualization in your web browser.',
  'pages.home.register': 'Register',
  'pages.home.signIn': 'Sign In',
  'pages.home.whyChoose': 'Why Choose StructuraMatrix Beam Designer?',
  'pages.home.multiSpanLabel': 'Multi-Span Analysis:',
  'pages.home.multiSpanText': 'Continuous beams with unlimited spans',
  'pages.home.eurocodeLabel': 'Eurocode Compliance:',
  'pages.home.eurocodeText': 'Steel, concrete, and timber code checking',
  'pages.home.interactiveLabel': 'Interactive Visualization:',
  'pages.home.interactiveText': 'Real-time results with professional diagrams',
  'pages.home.professionalLabel': 'Professional Results:',
  'pages.home.professionalText': 'Detailed engineering calculations and reports',
  'pages.home.technicalFeatures': 'Technical Features',
  'pages.home.feat1': 'Multiple load types (point, distributed, triangular)',
  'pages.home.feat2': 'Moment, shear, and deflection diagrams',
  'pages.home.feat3': 'Support reactions and utilization ratios',
  'pages.home.feat4': 'Load case management and combinations',

  // ----- About page -----
  'pages.about.subtitle': 'Professional Structural Analysis Platform',
  'pages.about.ourMission': 'Our Mission',
  'pages.about.missionText': 'Empowering engineers to work smarter, not harder.',
  'pages.about.whatWeOffer': 'What We Offer',
  'pages.about.beamDesigner': 'Beam Designer',
  'pages.about.beamDesignerText': 'Advanced continuous beam analysis with multi-span support and comprehensive results.',
  'pages.about.structuraBitsText': 'Educational articles on structural engineering theory, beautifully presented.',
  'pages.about.eurocodeCompliance': 'Eurocode Compliance',
  'pages.about.eurocodeText': 'Full code checking for steel, concrete, and timber structures per European standards.',
  'pages.about.visualResults': 'Visual Results',
  'pages.about.visualResultsText': 'Interactive diagrams for moments, shear forces, and deflections with professional output.',
  'pages.about.theTeam': 'The Team',
  'pages.about.teamIntro': 'StructuraMatrix is crafted by a multidisciplinary team of structural engineers and software architects who share a passion for delivering dependable analysis tools to practicing engineers.',
  'pages.about.teamDetail1': 'Our engineering group validates every release against Eurocode benchmarks and real-world case studies, ensuring the numerical core matches the expectations of professional practice.',
  'pages.about.teamDetail2': 'The product and experience crew focuses on clarity and usability, transforming complex engineering workflows into intuitive interfaces that help both seasoned professionals and emerging talent work with confidence.',
  'pages.about.logoStory': 'The story behind the StructuraMatrix logo',
  'pages.about.logoDesc1': 'The beauty of mathematics in modelling physical behaviour is the heartbeat of StructuraMatrix. Our shell-inspired form echoes the graceful curves of analytical solutions that engineers trace when predicting deflection, vibration, and equilibrium. Each contour is a reminder that equations are not abstract symbols, but living guides that reveal how real structures breathe under load.',
  'pages.about.logoDesc2': 'The grid that overlays the form nods to the collaborative craft of our team—structural engineers and software architects translating rigorous theory into dependable digital tools. Every intersecting line mirrors the synergy between numerical precision and human insight, capturing the story of how we turn complex behaviour into clarity for practicing professionals.',
  'pages.about.versionInfo': 'Version Information',
  'pages.about.currentVersion': 'Current Version:',
  'pages.about.releaseDate': 'Release Date:',
  'pages.about.releaseDateValue': 'October 2025',
  'pages.about.platform': 'Platform:',
  'pages.about.platformValue': 'Web Application',
  'pages.about.status': 'Status:',
  'pages.about.statusValue': 'Active Development',
  'pages.about.startToday': 'Start Using StructuraMatrix Today',
  'pages.about.ctaText': 'Join engineers worldwide who trust StructuraMatrix for their structural analysis needs.',
  'pages.about.tryBeamDesigner': 'Try Beam Designer',
  'pages.about.exploreStructuraBits': 'Explore Structura-Bits',

  // ===== Admin — shell =====
  'admin.panelTitle': 'Admin Panel',
  'admin.menu.samples': 'Sample Reports',
  'admin.menu.geometry': 'Geometry Library',
  'admin.menu.materials': 'Materials Library',
  'admin.menu.loads': 'Load Library',
  'admin.menu.library': 'Complete Beams',
  'admin.menu.structurabits': 'Structura-Bits Ordering',
  'admin.menu.users': 'User Management',
  'admin.menu.regression': 'Regression Tests',

  // ===== Admin — users section =====
  'admin.users.newUser': 'New User',
  'admin.users.introBefore': 'Manage StructuraMatrix user accounts stored in',
  'admin.users.introAfter': '. Creating or editing a user here updates their subscription tier and, if a password is provided, their local login credentials.',
  'admin.users.loading': 'Loading users...',
  'admin.users.thEmail': 'Email',
  'admin.users.thName': 'Name',
  'admin.users.thTier': 'Tier',
  'admin.users.thCreated': 'Created',
  'admin.users.thLastLogin': 'Last Login',
  'admin.users.thPassword': 'Password',
  'admin.users.thAdmin': 'Admin',
  'admin.users.thActive': 'Active',
  'admin.users.thActions': 'Actions',
  'admin.users.noUsers': 'No users found.',
  'admin.users.never': 'Never',
  'admin.users.passwordSet': 'Set',
  'admin.users.passwordNone': 'None',
  'admin.users.roleBadgeAdmin': 'Admin',
  'admin.users.roleBadgeUser': 'User',
  'admin.users.statusActive': 'Active',
  'admin.users.statusPending': 'Pending',
  'admin.users.createUser': 'Create User',
  'admin.users.editUser': 'Edit User',
  'admin.users.email': 'Email',
  'admin.users.nameOptional': 'Name (optional)',
  'admin.users.portalId': 'Portal ID',
  'admin.users.companyName': 'Company Name',
  'admin.users.contactEmail': 'Contact Email',
  'admin.users.companyPitch': 'Company Pitch',
  'admin.users.companyPitchPlaceholder': 'e.g. Engineering safe and sustainable structures',
  'admin.users.hqLocation': 'HQ Location',
  'admin.users.sectorFocus': 'Sector Focus',
  'admin.users.serviceKeywords': 'Service Keywords',
  'admin.users.certifications': 'Certifications',
  'admin.users.role': 'Role',
  'admin.users.roleDesigner': 'Designer',
  'admin.users.roleApproval': 'Approval',
  'admin.users.roleInfo': 'Info',
  'admin.users.roleAll': 'All',
  'admin.users.subscriptionTier': 'Subscription Tier',
  'admin.users.tierFree': 'Free',
  'admin.users.tierProfessional': 'Professional',
  'admin.users.tierEnterprise': 'Enterprise',
  'admin.users.grantAdmin': 'Grant admin access to this user',
  'admin.users.activateStrong': 'Activate user',
  'admin.users.activateRest': ' (required to log in)',
  'admin.users.password': 'Password',
  'admin.users.passwordKeepCurrent': '(leave blank to keep current)',
  'admin.users.passwordHelp': 'Sets or resets the local login password for this user.',
  'admin.users.clear': 'Clear',
  'admin.users.save': 'Save',
  'admin.users.pendingResets': 'Pending Password Resets ({count})',
  'admin.users.thRequested': 'Requested',
  'admin.users.thExpires': 'Expires',
  'admin.users.copyLink': 'Copy Link',
  'admin.users.copyLinkTitle': 'Copy reset link',
  'admin.users.invalidateTitle': 'Invalidate token',
  'admin.users.resetLinkCopied': 'Reset link copied to clipboard!',
  'admin.users.confirmDelete': 'Delete user {email}? This cannot be undone.',
  'admin.users.errLoad': 'Failed to load users',
  'admin.users.errLoadHttp': 'Failed to load users (HTTP {status})',
  'admin.users.errSave': 'Failed to save user',
  'admin.users.errSaveHttp': 'Failed to save user (HTTP {status})',
  'admin.users.errDelete': 'Failed to delete user',
  'admin.users.errDeleteHttp': 'Failed to delete user (HTTP {status}): {text}',

  // ----- Admin common -----
  'admin.common.editComingSoon': 'Edit (Coming Soon)',

  // ----- Admin geometry section -----
  'admin.geometry.title': 'Geometry Library Management',
  'admin.geometry.introBefore': 'Browse cross-section templates loaded from',
  'admin.geometry.introAfter': ', grouped by material. Editing tools for moment capacity and section properties will be added here.',
  'admin.geometry.loading': 'Loading geometry templates...',
  'admin.geometry.errLoad': 'Failed to load geometry templates',
  'admin.geometry.errLoadHttp': 'Failed to load geometry templates (HTTP {status})',
  'admin.geometry.steelSections': 'Steel Sections',
  'admin.geometry.concreteSections': 'Concrete Sections',
  'admin.geometry.timberSections': 'Timber Sections',
  'admin.geometry.thName': 'Name',
  'admin.geometry.thType': 'Type',
  'admin.geometry.noTemplatesBefore': 'No geometry templates found in',
  'admin.geometry.noTemplatesAfter': '.',
  'admin.geometry.badgeTemplate': 'Template',
  'admin.geometry.geometricProperties': 'Geometric Properties',
  'admin.geometry.heightH': 'Height (h)',
  'admin.geometry.widthB': 'Width (b)',
  'admin.geometry.webThicknessTw': 'Web Thickness (tw)',
  'admin.geometry.flangeThicknessTf': 'Flange Thickness (tf)',
  'admin.geometry.sectionProperties': 'Section Properties',
  'admin.geometry.areaA': 'Area (A)',
  'admin.geometry.momentInertiaIy': 'Moment of Inertia (Iy)',
  'admin.geometry.momentInertiaIz': 'Moment of Inertia (Iz)',
  'admin.geometry.elasticModulusWely': 'Elastic Modulus (Wel,y)',
  'admin.geometry.plasticModulusWply': 'Plastic Modulus (Wpl,y)',
  'admin.geometry.sectionClass': 'Section Class',
  'admin.geometry.momentCapacity': 'Moment Capacity',
  'admin.geometry.elasticCapacityMel': 'Elastic Capacity (Mel)',
  'admin.geometry.plasticCapacityMpl': 'Plastic Capacity (Mpl)',
  'admin.geometry.reinforcedCapacityMRd': 'Reinforced Capacity (MRd)',
  'admin.geometry.reinforcementProperties': 'Reinforcement Properties',
  'admin.geometry.bottomReinfArea': 'Bottom Reinforcement Area',
  'admin.geometry.effectiveCover': 'Effective Cover',
  'admin.geometry.effectiveDepthD': 'Effective Depth (d)',
  'admin.geometry.reinforcementRatioRho': 'Reinforcement Ratio (ρ)',

  // ----- Admin library section -----
  'admin.library.title': 'Complete Beam Library',
  'admin.library.introBefore': 'Pre-configured beam templates stored in',
  'admin.library.introAfter': ', grouped by material type. These templates include complete geometry, materials, loads, and code requirements.',
  'admin.library.loading': 'Loading beam templates...',
  'admin.library.errLoad': 'Failed to load beam templates',
  'admin.library.errLoadHttp': 'Failed to load beam templates (HTTP {status})',
  'admin.library.errLoadDetailsHttp': 'Failed to load template details (HTTP {status})',
  'admin.library.steelBeams': 'Steel Beams',
  'admin.library.concreteBeams': 'Concrete Beams',
  'admin.library.timberBeams': 'Timber Beams',
  'admin.library.thName': 'Name',
  'admin.library.thDescription': 'Description',
  'admin.library.thCategory': 'Category',
  'admin.library.thSpans': 'Spans',
  'admin.library.thOrderMaterial': 'Order (Material)',
  'admin.library.spanUnit': 'spans',
  'admin.library.noTemplatesBefore': 'No beam templates found in',
  'admin.library.noTemplatesAfter': '. The database will be seeded on next backend start.',
  'admin.library.loadingDetails': 'Loading details...',
  'admin.library.tabGeometry': 'Geometry',
  'admin.library.tabMaterial': 'Material',
  'admin.library.tabLoads': 'Loads',
  'admin.library.tabFullConfig': 'Full Config',
  'admin.library.spansAndNodes': 'Spans and Nodes',
  'admin.library.thSpan': 'Span',
  'admin.library.thLengthM': 'Length (m)',
  'admin.library.spanRow': 'Span {n}',
  'admin.library.thNode': 'Node',
  'admin.library.thSupportType': 'Support Type',
  'admin.library.thPositionM': 'Position (m)',
  'admin.library.nodeRow': 'Node {n}',
  'admin.library.typeLabel': 'Type:',
  'admin.library.heightLabel': 'Height:',
  'admin.library.widthLabel': 'Width:',
  'admin.library.materialType': 'Material Type',
  'admin.library.grade': 'Grade',
  'admin.library.concreteClass': 'Concrete Class',
  'admin.library.timberClass': 'Timber Class',
  'admin.library.elasticModulus': 'Elastic Modulus',
  'admin.library.density': 'Density',
  'admin.library.loadCases': 'Load Cases',
  'admin.library.loadCombinations': 'Load Combinations',

  // ----- Admin materials section -----
  'admin.materials.title': 'Materials Library Management',
  'admin.materials.introBefore': 'Browse material properties loaded from',
  'admin.materials.introAfter': ' - Eurocode-compliant material data for structural design.',
  'admin.materials.loading': 'Loading materials...',
  'admin.materials.errLoadSummary': 'Failed to load materials summary',
  'admin.materials.errLoadSummaryHttp': 'Failed to load materials summary (HTTP {status})',
  'admin.materials.errLoadCategory': 'Failed to load {category} materials',
  'admin.materials.errLoadCategoryHttp': 'Failed to load {category} materials (HTTP {status})',
  'admin.materials.librarySummary': 'Library Summary',
  'admin.materials.steelGrades': 'Steel Grades',
  'admin.materials.concreteClasses': 'Concrete Classes',
  'admin.materials.timberClasses': 'Timber Classes',
  'admin.materials.reinforcement': 'Reinforcement',
  'admin.materials.prestressing': 'Prestressing',
  'admin.materials.total': 'Total',
  'admin.materials.btnSteel': 'Steel',
  'admin.materials.btnConcrete': 'Concrete',
  'admin.materials.btnTimber': 'Timber',
  'admin.materials.btnReinforcement': 'Reinforcement',
  'admin.materials.btnPrestressing': 'Prestressing',
  'admin.materials.steelTitle': 'Steel Materials (EN 1993-1-1)',
  'admin.materials.concreteTitle': 'Concrete Materials (EN 1992-1-1)',
  'admin.materials.timberTitle': 'Timber Materials (EN 338)',
  'admin.materials.reinforcementTitle': 'Reinforcement Steel (EN 1992-1-1)',
  'admin.materials.prestressingTitle': 'Prestressing Steel (EN 1992-1-1)',
  'admin.materials.thGrade': 'Grade',
  'admin.materials.thSubgrade': 'Subgrade',
  'admin.materials.thProductForm': 'Product Form',
  'admin.materials.thClass': 'Class',
  'admin.materials.thKind': 'Kind',
  'admin.materials.thDuctilityClass': 'Ductility Class',
  'admin.materials.thRelaxationClass': 'Relaxation Class',
  'admin.materials.footerSteel': '{count} steel materials available',
  'admin.materials.footerConcrete': '{count} concrete classes available',
  'admin.materials.footerTimber': '{count} timber classes available',
  'admin.materials.footerReinf': '{count} reinforcement steel grades available',
  'admin.materials.footerPrestress': '{count} prestressing steel grades available',

  // ----- Admin cooperative users -----
  'admin.coop.title': 'Cooperative Users',
  'admin.coop.saveFirst': 'Save the user first, then you can add cooperative users',
  'admin.coop.placeholderUserId': 'User ID (GUID)',
  'admin.coop.placeholderEmail': 'Email',
  'admin.coop.placeholderAuth0': 'Auth0 ID (optional)',
  'admin.coop.placeholderName': 'Name (optional)',
  'admin.coop.add': 'Add',
  'admin.coop.noCoopUsers': 'No cooperative users',
  'admin.coop.thAction': 'Action',
  'admin.coop.errUserIdEmailRequired': 'User ID and Email are required',
  'admin.coop.errLoad': 'Failed to load cooperative users',
  'admin.coop.errAdd': 'Failed to add cooperative user',
  'admin.coop.errDelete': 'Failed to delete cooperative user',
  'admin.coop.confirmRemove': 'Remove this cooperative user?',

  // ----- Admin Structura-Bits ordering -----
  'admin.bits.intro': 'Inspect and control the ordering of Structura-Bits as shown on the public page. Use the sort selector to verify the sequence by date, title, or category.',
  'admin.bits.loading': 'Loading Structura-Bits...',
  'admin.bits.errLoad': 'Failed to load Structura-Bits',
  'admin.bits.errSave': 'Failed to save Structura-Bits ordering',
  'admin.bits.noBits': 'No Structura-Bits found.',
  'admin.bits.saved': 'Saved ordering for {count} Structura-Bits.',
  'admin.bits.orderBy': 'Order by:',
  'admin.bits.sortExplicit': 'Explicit Order (saved)',
  'admin.bits.sortDate': 'Date (newest first)',
  'admin.bits.sortTitle': 'Title (A → Z)',
  'admin.bits.sortCategory': 'Category, then Title',
  'admin.bits.saveOrdering': 'Save Ordering',
  'admin.bits.thOrder': 'Order',
  'admin.bits.thTitle': 'Title',
  'admin.bits.thDate': 'Date',
  'admin.bits.thCategory': 'Category',
  'admin.bits.thReadingTime': 'Reading Time',
  'admin.bits.thSlug': 'Slug',

  // ----- Admin loads section -----
  'admin.loads.title': 'Load Library Management',
  'admin.loads.introBefore': 'Browse load cases stored in',
  'admin.loads.introAfter': ', grouped by category. Editing tools will be extended to support detailed load pattern adjustments.',
  'admin.loads.loading': 'Loading load cases...',
  'admin.loads.errLoad': 'Failed to load load cases',
  'admin.loads.errLoadHttp': 'Failed to load load cases (HTTP {status})',
  'admin.loads.loadCasesSuffix': 'Load Cases',
  'admin.loads.thName': 'Name',
  'admin.loads.thActive': 'Active',
  'admin.loads.thOrder': 'Order',
  'admin.loads.thLoads': 'Loads',
  'admin.loads.statusInactive': 'Inactive',
  'admin.loads.noLoadCasesBefore': 'No load cases found in',
  'admin.loads.noLoadCasesAfter': '.',

  // ----- Admin samples section -----
  'admin.samples.title': 'Sample Report Generation',
  'admin.samples.intro': 'Generate sample calculation reports for testing and verification purposes. This will create PDF reports for steel, concrete, and timber materials.',
  'admin.samples.generating': 'Generating Samples...',
  'admin.samples.run': 'Run Sample Generation',
  'admin.samples.sampleTypes': 'Sample Types:',
  'admin.samples.beamReportSuffix': 'Beam Report',
  'admin.samples.open': 'Open',
  'admin.samples.steelBeamReport': 'Steel Beam Report',
  'admin.samples.concreteBeamReport': 'Concrete Beam Report',
  'admin.samples.timberBeamReport': 'Timber Beam Report',
  'admin.samples.errGenerate': 'Failed to generate sample reports',
  'admin.samples.generated': 'Generated {count} sample report(s) in {directory}.',
  'admin.samples.failed': 'Sample generation failed: {message}',
  'admin.samples.serverOutputDir': 'server output directory',

  // ----- Admin regression panel -----
  'admin.regression.title': 'Regression Testing',
  'admin.regression.runningTests': 'Running Tests...',
  'admin.regression.runAllTests': 'Run All Tests',
  'admin.regression.waitingToRun': 'Waiting to run...',
  'admin.regression.testSteel': 'Steel Beam - Buckling & Axial',
  'admin.regression.testConcrete': 'Concrete Beam - Dynamics',
  'admin.regression.testTimber': 'Timber Floor - Vibration',

  // ===== Auth — common =====
  'auth.common.loading': 'Loading...',
  'auth.common.cancel': 'Cancel',
  'auth.common.networkError': 'Network error. Please check your connection and try again.',

  // ----- Auth nav actions -----
  'auth.nav.register': 'Register',
  'auth.nav.previewRegister': 'Preview Register',
  'auth.nav.signIn': 'Sign In',
  'auth.nav.accountAlt': 'Account',
  'auth.nav.myAccount': 'My Account',
  'auth.nav.accountDashboard': 'Account Dashboard',
  'auth.nav.beamDesigner': 'Beam Designer',
  'auth.nav.signOut': 'Sign Out',

  // ----- Auth route guards -----
  'auth.route.signInToPage': 'Please sign in to access this page.',
  'auth.route.noAdminPermission': 'You do not have permission to access the Admin area.',

  // ----- Forgot password -----
  'auth.forgot.errProcess': 'Unable to process request. Please try again.',
  'auth.forgot.checkEmail': 'Check your email',
  'auth.forgot.checkEmailBefore': 'If an account exists for',
  'auth.forgot.checkEmailAfter': ', you will receive password reset instructions shortly.',
  'auth.forgot.noEmailHint': "Didn't receive an email? Check your spam folder or contact support.",
  'auth.forgot.backToSignIn': 'Back to Sign In',
  'auth.forgot.title': 'Forgot password?',
  'auth.forgot.subtitle': "Enter your email and we'll send you instructions to reset your password.",
  'auth.forgot.emailAddress': 'Email address',
  'auth.forgot.sending': 'Sending...',
  'auth.forgot.sendInstructions': 'Send Reset Instructions',

  // ----- Reset password -----
  'auth.reset.errMismatch': 'Passwords do not match.',
  'auth.reset.errMinChars': 'Password must be at least 6 characters.',
  'auth.reset.errReset': 'Unable to reset password. The link may have expired.',
  'auth.reset.invalidLink': 'Invalid Reset Link',
  'auth.reset.invalidLinkText': 'This password reset link is invalid or has expired.',
  'auth.reset.requestNewLink': 'Request New Reset Link',
  'auth.reset.done': 'Password Reset!',
  'auth.reset.doneText': 'Your password has been successfully reset. Redirecting to sign in...',
  'auth.reset.redirecting': 'Redirecting...',
  'auth.reset.setNew': 'Set New Password',
  'auth.reset.setNewSubtitle': 'Enter your new password below.',
  'auth.reset.newPassword': 'New Password',
  'auth.reset.newPasswordPlaceholder': 'Enter new password',
  'auth.reset.confirmPassword': 'Confirm Password',
  'auth.reset.confirmPasswordPlaceholder': 'Confirm new password',
  'auth.reset.resetting': 'Resetting...',
  'auth.reset.resetButton': 'Reset Password',
  'auth.register.pendingTitle': 'Check your e-mail',
  'auth.register.pendingBody': 'We have sent you a verification link. Click it to activate the account and sign in.',
  'auth.verify.title': 'Verify e-mail address',
  'auth.verify.verifying': 'Verifying e-mail address…',
  'auth.verify.success': 'E-mail address verified!',
  'auth.verify.successText': 'You are now signed in.',
  'auth.verify.goToApp': 'Go to the app',
  'auth.verify.invalidLink': 'Invalid verification link',
  'auth.verify.invalidLinkText': 'This verification link is invalid or has expired. Register again or request a new link from the account page.',

  // ----- Pending activation -----
  'auth.pending.processing': 'Processing...',
  'auth.pending.welcome': 'Welcome to StructuraMatrix!',
  'auth.pending.thanksBefore': 'Thank you for registering,',
  'auth.pending.thanksAfter': '!',
  'auth.pending.statusBadge': 'Account Pending Activation',
  'auth.pending.reviewNote': "Your account is being reviewed by our team. You'll receive access shortly!",
  'auth.pending.whatsWaiting': "What's waiting for you:",
  'auth.pending.benefit1Label': '30 days free trial',
  'auth.pending.benefit1Rest': ' — Full access to all professional features',
  'auth.pending.benefit2Label': 'Continuous Beam Designer',
  'auth.pending.benefit2Rest': ' — Multi-span analysis with Eurocode compliance',
  'auth.pending.benefit3Label': 'Professional Reports',
  'auth.pending.benefit3Rest': ' — Generate calculation documentation',
  'auth.pending.benefit4Label': 'Steel, Concrete & Timber',
  'auth.pending.benefit4Rest': ' — Code checks for multiple materials',
  'auth.pending.benefit5Label': 'After trial:',
  'auth.pending.benefit5Rest': ' €110 or 1 100 NOK per month — Cancel anytime',
  'auth.pending.activationTimeLabel': 'Typical activation time:',
  'auth.pending.activationTimeRest': ' Within 24 hours during business days',
  'auth.pending.questions': 'Questions? Contact us at',

  // ----- Register -----
  'auth.register.benefit1Title': 'Easy learning curve',
  'auth.register.benefit1Desc': 'You’ll already be up and running once you decide to move forward.',
  'auth.register.benefit2Title': 'Identify daily value',
  'auth.register.benefit2Desc': 'See how automated combinations, diagrams, and reports slot into your current projects and save real hours.',
  'auth.register.benefit3Title': 'Expand your expertise',
  'auth.register.benefit3Desc': 'Gain insights from advanced material models and Eurocode checks that elevate your engineering decisions.',
  'auth.register.success': 'Registration successful. You are now signed in and can start using Beam Designer.',
  'auth.register.errGeneric': 'Unable to complete registration right now.',
  'auth.register.heroAuthenticated': 'You already have access to StructuraMatrix.',
  'auth.register.heroAnonymous': 'Create a free account to start analyzing beams with StructuraMatrix.',
  'auth.register.badgeEngineers': 'Engineers',
  'auth.register.badgeSr': 'Engineers love StructuraMatrix',
  'auth.register.startTrial': 'Start your 30-day free trial',
  'auth.register.noCreditCard': 'No credit card required • Full access for 30 days',
  'auth.register.alreadyRegistered': 'You are already registered',
  'auth.register.registerNow': 'Register Now',
  'auth.register.email': 'Email',
  'auth.register.nameOptional': 'Name (optional)',
  'auth.register.companyOptional': 'Company Name (optional)',
  'auth.register.password': 'Password',
  'auth.register.viewAccount': 'View Account',
  'auth.register.whyTrial': 'Why sign up for a free trial',
  'auth.register.pricingTitle': 'Simple, transparent pricing',
  'auth.register.pricingSubtitle': 'After your free trial, continue with a monthly subscription',
  'auth.register.professional': 'Professional',
  'auth.register.thirtyDayTrial': '30 days free trial',
  'auth.register.perMonth': '/month',
  'auth.register.or': 'or',
  'auth.register.feat1': 'Unlimited beam analyses',
  'auth.register.feat2': 'Multi-span continuous beams',
  'auth.register.feat3': 'Steel, Concrete & Timber code checks',
  'auth.register.feat4': 'Eurocode with National Annexes',
  'auth.register.feat5': 'Professional PDF reports',
  'auth.register.feat6': 'Priority support',
  'auth.register.cancelAnytime': 'Cancel anytime • No commitment',
  'auth.register.pricingQuestions': 'Questions about pricing?',
  'auth.register.contactUs': 'Contact us',

  // ----- Cooperative users (account page) -----
  'auth.coop.title': 'Cooperative Users',
  'auth.coop.addUser': 'Add User',
  'auth.coop.perAppBefore': 'Cooperators are managed per app — they apply only to your',
  'auth.coop.perAppAfter': ' analyses. To grant the same person access in the other apps, add them there as well.',
  'auth.coop.addCoopUser': 'Add Cooperative User',
  'auth.coop.labelUserId': 'User ID (GUID)',
  'auth.coop.labelEmail': 'Email',
  'auth.coop.labelAuth0': 'Auth0 ID',
  'auth.coop.labelName': 'Name',
  'auth.coop.labelRole': 'Role',
  'auth.coop.roleDesigner': 'Designer',
  'auth.coop.roleApproval': 'Approval',
  'auth.coop.roleInfo': 'Info',
  'auth.coop.roleAll': 'All',
  'auth.coop.adding': 'Adding...',
  'auth.coop.add': 'Add',
  'auth.coop.noUsersYet': 'No cooperative users added yet',
  'auth.coop.thName': 'Name',
  'auth.coop.thEmail': 'Email',
  'auth.coop.thUserId': 'User ID',
  'auth.coop.thAuth0': 'Auth0 ID',
  'auth.coop.thRole': 'Role',
  'auth.coop.thActions': 'Actions',
  'auth.coop.errLoad': 'Failed to load cooperative users',
  'auth.coop.errAdd': 'Failed to add cooperative user',
  'auth.coop.errDelete': 'Failed to delete cooperative user',
  'auth.coop.errUserIdEmailRequired': 'User ID and Email are required',
  'auth.coop.confirmRemove': 'Remove this cooperative user?',

  // ----- Account page -----
  'auth.account.errLogin': 'Login failed.',
  'auth.account.errUpdateProfile': 'Failed to update profile',
  'auth.account.profileUpdated': 'Profile updated successfully. Refreshing...',
  'auth.account.updateFailed': 'Update failed',
  'auth.account.errUploadAvatar': 'Failed to upload avatar ({status}). {text}',
  'auth.account.errUploadAvatarGeneric': 'Failed to upload avatar.',
  'auth.account.errUploadLogo': 'Failed to upload logo ({status}). {text}',
  'auth.account.errUploadLogoGeneric': 'Failed to upload logo.',
  'auth.account.welcomeBack': 'Welcome Back',
  'auth.account.signInSubtitle': 'Sign in to access your account dashboard and manage your subscription.',
  'auth.account.emailAddress': 'Email Address',
  'auth.account.password': 'Password',
  'auth.account.passwordPlaceholder': 'Enter your password',
  'auth.account.signingIn': 'Signing in...',
  'auth.account.newToStructura': 'New to StructuraMatrix?',
  'auth.account.createAccountLink': 'Create a free account',
  'auth.account.createAccountRest': ' and start analyzing beams today.',
  'auth.account.featAccessTools': 'Access beam analysis tools',
  'auth.account.featManageSub': 'Manage your subscription tier',
  'auth.account.featDownloadReports': 'Download generated reports',
  'auth.account.unavailable': 'Account information is currently unavailable.',
  'auth.account.manageSubtitle': 'Manage your personal profile, company details, and subscription settings',
  'auth.account.personalInfo': 'Personal Info',
  'auth.account.edit': 'Edit',
  'auth.account.avatarPreviewAlt': 'Avatar Preview',
  'auth.account.uploadPhoto': 'Upload Photo',
  'auth.account.avatarUrlOptional': 'Avatar URL (optional)',
  'auth.account.yourName': 'Your Name',
  'auth.account.email': 'Email',
  'auth.account.competenceSkills': 'Competence / Skills',
  'auth.account.competencePlaceholder': 'Structural Engineering, FEA, Python...',
  'auth.account.saving': 'Saving...',
  'auth.account.saveChanges': 'Save Changes',
  'auth.account.name': 'Name',
  'auth.account.competence': 'Competence',
  'auth.account.companyProfile': 'Company Profile',
  'auth.account.companyLogo': 'Company Logo',
  'auth.account.companyLogoPreviewAlt': 'Company Logo Preview',
  'auth.account.companyName': 'Company Name',
  'auth.account.portalIdInternal': 'Portal ID (Internal)',
  'auth.account.portalIdHint': 'This is your internal identifier for StructuraMatrix.',
  'auth.account.contactEmail': 'Contact Email',
  'auth.account.hqLocation': 'HQ Location',
  'auth.account.companyPitch': 'Company Pitch',
  'auth.account.companyPitchPlaceholder': 'e.g. Engineering safe and sustainable structures for tomorrow',
  'auth.account.sectorFocus': 'Sector Focus',
  'auth.account.serviceKeywords': 'Service Keywords',
  'auth.account.certifications': 'Certifications',
  'auth.account.role': 'Role',
  'auth.account.companyLogoAlt': 'Company Logo',
  'auth.account.dtPortalId': 'Portal ID',
  'auth.account.dtContact': 'Contact',
  'auth.account.dtFocus': 'Focus',
  'auth.account.dtServices': 'Services',
  'auth.account.dtSubscription': 'Subscription',
  'auth.account.tierPlan': '{tier} Plan',
  'auth.account.memberSince': 'Member since',
  'auth.account.renew': '{date} (Renew)',

  // ===== Structura-Bits =====
  'bits.list.errLoad': 'Failed to load articles. Please try again later.',
  'bits.list.loading': 'Loading Structura-Bits...',
  'bits.list.error': '⚠️ Error',
  'bits.list.subtitle': 'Short, digestible, yet informative articles on structural engineering theory and practice',
  'bits.list.noArticles': 'No articles yet',
  'bits.list.checkBack': 'Check back soon for insightful articles on structural engineering!',
  'bits.article.errLoad': 'Article not found or failed to load.',
  'bits.article.loading': 'Loading article...',
  'bits.article.notFound': '⚠️ Article Not Found',
  'bits.article.backArrow': '← Back to Structura-Bits',
  'bits.article.back': 'Back to Structura-Bits',
  'bits.article.equation': 'Equation',
  'bits.article.table': 'Table',
  'bits.article.scrollHint': 'Scroll sideways to see more',
  'bits.article.footerNote': 'This Structura-Bit is part of the StructuraMatrix knowledge base.',

  // ===== Error boundary (fallback UI) =====
  'ui.errorBoundary.title': 'Something went wrong',
  'ui.errorBoundary.errorLabel': 'Error:',
  'ui.errorBoundary.stackLabel': 'Stack:',
  'ui.errorBoundary.reload': 'Reload Page',
  // Analysis action alerts and result-banner summary (added 2026-07-04 UI audit)
  'beam.analysis.alert.axialNoSupport': '⚠️ Axial force loads detected, but no axial support found!\n\nThe beam has axial loads applied but no support that can resist horizontal movement.\n\nPlease add at least one support with "Axial Fixed" enabled.',
  'beam.analysis.alert.materialLoadFailed': '⚠️ Material properties failed to load from the database!\n\nPlease check the Materials tab and ensure a valid material is selected.',
  'beam.analysis.alert.dynamicsNeedsAnalysis': 'Dynamics needs a stored analysis. Run the analysis first, then calculate dynamics.',
  'beam.analysis.alert.materialConfigError': '⚠️ Material configuration error:\n\n{message}',
  'beam.analysis.alert.analyzeAllFailed': '❌ Multi-limit-state analysis failed:\n\n{message}',
  'beam.analysis.alert.timoshenkoFailed': '❌ Timoshenko analysis failed:\n\n{message}',
  'beam.analysis.alert.missingAnalysisId': 'Analysis ID is missing. Please run the analysis again to obtain an AnalysisId.',
  'beam.analysis.alert.responseTimeout': 'Response calculation timed out. The request is taking too long — please try a simpler model.',
  'beam.analysis.alert.responseFailed': 'Response calculation failed: {message}',
  'beam.analysis.alert.secondOrderFailed': 'Second-order effects calculation failed: {message}',
  'beam.analysis.alert.dynamicsFailed': 'Dynamics calculation failed: {message}',
  'beam.analysis.summary.allAnalyzed': 'ULS and SLS limit states analyzed, {count} load combinations calculated.',
  'beam.analysis.summary.timoshenkoAnalyzed': 'Timoshenko theory: ULS and SLS limit states analyzed, {count} load combinations.',
  'beam.analysis.summary.slenderness': 'Slenderness ratio L/h = {ratio} (shear deformation {significance})',
  'beam.analysis.summary.shearSignificant': 'significant',
  'beam.analysis.summary.shearMinimal': 'minimal',
  'beam.analysis.summary.shearModerate': 'moderate',
  'beam.analysis.summary.combinations': 'Combinations:',
  'beam.analysis.summary.leading': 'Leading {category}',
  'beam.response.notComputable': 'Not computable'
}
